import proj4 from "proj4";

const ETOPO_IMAGE_URL = "https://gis.ngdc.noaa.gov/arcgis/rest/services/DEM_mosaics/DEM_global_mosaic/ImageServer";

const CRS_DEFS = [
  { code: "EPSG:4326", proj4: "+proj=longlat +datum=WGS84 +no_defs +type=crs" },
  { code: "EPSG:3857", proj4: "+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs +type=crs" },
  { code: "EPSG:2273", proj4: "+proj=lcc +lat_0=31.8333333333333 +lon_0=-81 +lat_1=34.8333333333333 +lat_2=32.5 +x_0=609600 +y_0=0 +ellps=GRS80 +datum=NAD83 +units=us-ft +no_defs +type=crs" },
  { code: "EPSG:32610", proj4: "+proj=utm +zone=10 +datum=WGS84 +units=m +no_defs +type=crs" },
  { code: "EPSG:32611", proj4: "+proj=utm +zone=11 +datum=WGS84 +units=m +no_defs +type=crs" },
  { code: "EPSG:26910", proj4: "+proj=utm +zone=10 +datum=NAD83 +units=m +no_defs +type=crs" },
  { code: "EPSG:26911", proj4: "+proj=utm +zone=11 +datum=NAD83 +units=m +no_defs +type=crs" },
];

for (let i = 0; i < CRS_DEFS.length; i += 1) {
  proj4.defs(CRS_DEFS[i].code, CRS_DEFS[i].proj4);
}

export async function buildEtopoSurfaceFromAoi(aoiRingLonLat, gridSize, onProgress = null) {
  if (!Array.isArray(aoiRingLonLat) || aoiRingLonLat.length < 3) {
    throw new Error("AOI polygon ring is required.");
  }
  const points = aoiRingLonLat.map(([lon, lat]) => ({ lon: Number(lon), lat: Number(lat) }))
    .filter((p) => Number.isFinite(p.lon) && Number.isFinite(p.lat));
  if (points.length < 3) {
    throw new Error("AOI polygon has no valid coordinates.");
  }

  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i];
    minLon = Math.min(minLon, p.lon);
    maxLon = Math.max(maxLon, p.lon);
    minLat = Math.min(minLat, p.lat);
    maxLat = Math.max(maxLat, p.lat);
  }
  if (!(minLon < maxLon && minLat < maxLat)) {
    throw new Error("AOI extent is invalid.");
  }

  const n = Math.max(20, Math.min(128, Math.round(Number(gridSize) || 36)));
  const width = n;
  const height = n;
  const lonCenter = (minLon + maxLon) * 0.5;
  const latCenter = (minLat + maxLat) * 0.5;
  const mPerDegLat = 111_132;
  const mPerDegLon = 111_320 * Math.cos((latCenter * Math.PI) / 180);
  const minX = (minLon - lonCenter) * mPerDegLon;
  const maxX = (maxLon - lonCenter) * mPerDegLon;
  const minZ = (minLat - latCenter) * mPerDegLat;
  const maxZ = (maxLat - latCenter) * mPerDegLat;
  const values = new Float32Array(width * height);

  const requests = [];
  for (let iz = 0; iz < height; iz += 1) {
    const tz = iz / Math.max(1, height - 1);
    const lat = minLat + (maxLat - minLat) * tz;
    for (let ix = 0; ix < width; ix += 1) {
      const tx = ix / Math.max(1, width - 1);
      const lon = minLon + (maxLon - minLon) * tx;
      requests.push({ ix, iz, lon, lat });
    }
  }

  const maxConcurrent = 14;
  let cursor = 0;
  let completed = 0;
  let failedSamples = 0;

  async function worker() {
    while (cursor < requests.length) {
      const idx = cursor;
      cursor += 1;
      const req = requests[idx];
      const value = await fetchEtopoValue(req.lon, req.lat);
      if (!Number.isFinite(value)) {
        failedSamples += 1;
      }
      const depth = Number.isFinite(value) ? value : Number.NaN;
      values[req.iz * width + req.ix] = depth;
      completed += 1;
      onProgress?.(completed, requests.length);
    }
  }

  const workers = [];
  for (let i = 0; i < maxConcurrent; i += 1) {
    workers.push(worker());
  }
  await Promise.all(workers);
  const fillStats = fillMissingCells(values, width, height);
  const stats = computeStats(values);
  if (!Number.isFinite(stats.min) || !Number.isFinite(stats.max)) {
    throw new Error("ETOPO samples unavailable for this AOI.");
  }
  if (stats.max - stats.min < 0.25) {
    throw new Error("ETOPO surface is nearly flat for this AOI/resolution.");
  }

  return {
    version: 1,
    source: "etopo",
    width,
    height,
    minX,
    maxX,
    minZ,
    maxZ,
    values,
    failedSamples,
    filledSamples: fillStats.filled,
    sampleCount: requests.length,
    minDepth: stats.min,
    maxDepth: stats.max,
  };
}

export function parseAoiRingFromGeoJson(rawText) {
  const parsed = JSON.parse(rawText);
  const feature = parsed?.type === "FeatureCollection"
    ? parsed.features?.find((f) => f?.geometry?.type === "Polygon")
    : parsed?.type === "Feature" && parsed?.geometry?.type === "Polygon"
      ? parsed
      : parsed?.type === "Polygon"
        ? { type: "Feature", geometry: parsed }
        : null;

  const ring = feature?.geometry?.coordinates?.[0];
  if (!Array.isArray(ring) || ring.length < 3) {
    throw new Error("No polygon ring found in GeoJSON.");
  }
  const sourceCrs = String(
    parsed?.crs?.properties?.name
      || feature?.crs?.properties?.name
      || "",
  ).toUpperCase();
  const normalized = ring.map((coord) => [Number(coord[0]), Number(coord[1])]);
  return normalizeRingLonLat(normalized, sourceCrs);
}

async function fetchEtopoValue(lon, lat) {
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const value = await tryFetchEtopoValue(lon, lat);
    if (Number.isFinite(value)) {
      return value;
    }
    if (attempt < 2) {
      await sleep(140 * (attempt + 1));
    }
  }
  return null;
}

async function tryFetchEtopoValue(lon, lat) {
  const geometry = encodeURIComponent(JSON.stringify({
    x: lon,
    y: lat,
    spatialReference: { wkid: 4326 },
  }));
  const url = `${ETOPO_IMAGE_URL}/identify?f=json&geometry=${geometry}&geometryType=esriGeometryPoint&sr=4326&returnGeometry=false`;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      return null;
    }
    const data = await res.json();
    if (Number.isFinite(Number(data?.value))) {
      return Number(data.value);
    }
    if (Number.isFinite(Number(data?.pixelValue))) {
      return Number(data.pixelValue);
    }
    if (Number.isFinite(Number(data?.properties?.value))) {
      return Number(data.properties.value);
    }
    return null;
  } catch {
    return null;
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeRingLonLat(ring, sourceCrs) {
  if (!ring.length) {
    return ring;
  }

  if (sourceCrs) {
    const from = normalizeCrsCode(sourceCrs);
    if (from === "EPSG:4326" || from === "CRS84") {
      return ensureLonLatRange(ring);
    }
    if (from === "EPSG:3857") {
      return ensureLonLatRange(ring.map(([x, y]) => webMercatorToLonLat(x, y)));
    }
    if (proj4.defs(from)) {
      const converted = ring.map(([x, y]) => proj4(from, "EPSG:4326", [x, y]));
      return ensureLonLatRange(converted);
    }
    throw new Error(`Unsupported AOI CRS (${sourceCrs}). Export AOI as EPSG:4326 GeoJSON.`);
  }

  let lonLatValid = 0;
  let latLonValid = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const [a, b] = ring[i];
    if (Number.isFinite(a) && Number.isFinite(b)) {
      if (Math.abs(a) <= 180 && Math.abs(b) <= 90) {
        lonLatValid += 1;
      }
      if (Math.abs(a) <= 90 && Math.abs(b) <= 180) {
        latLonValid += 1;
      }
    }
  }

  const mostlyProjected = lonLatValid < Math.max(3, ring.length * 0.2) && latLonValid < Math.max(3, ring.length * 0.2);
  if (mostlyProjected) {
    return ring.map(([x, y]) => webMercatorToLonLat(x, y));
  }

  if (latLonValid > lonLatValid * 1.4) {
    return ensureLonLatRange(ring.map(([lat, lon]) => [lon, lat]));
  }

  return ensureLonLatRange(ring);
}

function webMercatorToLonLat(x, y) {
  const lon = (x / 20037508.34) * 180;
  const lat = (Math.atan(Math.exp((y / 20037508.34) * Math.PI)) * 360) / Math.PI - 90;
  return [lon, lat];
}

function ensureLonLatRange(ring) {
  let valid = 0;
  for (let i = 0; i < ring.length; i += 1) {
    const [lon, lat] = ring[i];
    if (Math.abs(lon) <= 180 && Math.abs(lat) <= 90) {
      valid += 1;
    }
  }
  if (valid < Math.max(3, ring.length * 0.8)) {
    throw new Error("AOI coordinates are not valid lon/lat. Export AOI as EPSG:4326 GeoJSON.");
  }
  return ring;
}

function normalizeCrsCode(value) {
  const raw = String(value || "").toUpperCase();
  if (raw.startsWith("EPSG:")) {
    return raw;
  }
  const match = raw.match(/EPSG[:/](\d+)/);
  if (match) {
    return `EPSG:${match[1]}`;
  }
  return raw;
}

function fillMissingCells(values, width, height) {
  const total = width * height;
  let filled = 0;
  for (let pass = 0; pass < 8; pass += 1) {
    let changed = 0;
    for (let i = 0; i < total; i += 1) {
      if (Number.isFinite(values[i])) {
        continue;
      }
      const x = i % width;
      const z = Math.floor(i / width);
      let sum = 0;
      let count = 0;
      for (let dz = -1; dz <= 1; dz += 1) {
        for (let dx = -1; dx <= 1; dx += 1) {
          if (dx === 0 && dz === 0) {
            continue;
          }
          const nx = x + dx;
          const nz = z + dz;
          if (nx < 0 || nx >= width || nz < 0 || nz >= height) {
            continue;
          }
          const nv = values[nz * width + nx];
          if (Number.isFinite(nv)) {
            sum += nv;
            count += 1;
          }
        }
      }
      if (count > 0) {
        values[i] = sum / count;
        changed += 1;
        filled += 1;
      }
    }
    if (changed === 0) {
      break;
    }
  }

  const stats = computeStats(values);
  const fallback = Number.isFinite(stats.mean) ? stats.mean : -80;
  for (let i = 0; i < total; i += 1) {
    if (!Number.isFinite(values[i])) {
      values[i] = fallback;
      filled += 1;
    }
  }
  return { filled };
}

function computeStats(values) {
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  let n = 0;
  for (let i = 0; i < values.length; i += 1) {
    const v = values[i];
    if (!Number.isFinite(v)) {
      continue;
    }
    min = Math.min(min, v);
    max = Math.max(max, v);
    sum += v;
    n += 1;
  }
  return {
    min: n > 0 ? min : Number.NaN,
    max: n > 0 ? max : Number.NaN,
    mean: n > 0 ? sum / n : Number.NaN,
  };
}
